import * as THREE from 'three';
import { ConvexHull } from 'three/examples/jsm/math/ConvexHull.js';

/**
 * @param {THREE.Vector3[]} points - 3D points near the waterline
 * @returns {THREE.Vector3[]} - array of hull vertices in order
 */
export const computeConvexHull = (points: THREE.Vector3[]): THREE.Vector3[] => {
  const hull = new ConvexHull();
  hull.setFromPoints(points);

  const vertices: THREE.Vector3[] = [];

  // Iterate over each face of the convex hull
  hull.faces.forEach((face) => {
    // Each face has an edge property that points to one of its half-edges.
    let edge = face.edge;
    do {
      // edge.head is a THREE.Vector3 vertex on the face
      vertices.push(edge.head().point.clone());
      edge = edge.next;
    } while (edge !== face.edge);
  });

  // Remove duplicates based on a 2D projection (x,z) with a precision threshold
  const unique: THREE.Vector3[] = [];
  const seen = new Set();
  vertices.forEach((v) => {
    const key = `${v.x.toFixed(3)}_${v.z.toFixed(3)}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(new THREE.Vector3(v.x, v.y, v.z));
    }
  });

  // Sort the unique vertices in order around the center (using XZ-plane)
  const center = unique
    .reduce((acc, v) => acc.add(v), new THREE.Vector3())
    .divideScalar(unique.length);
  unique.sort((a, b) => {
    const angleA = Math.atan2(a.z - center.z, a.x - center.x);
    const angleB = Math.atan2(b.z - center.z, b.x - center.x);
    return angleA - angleB;
  });

  return unique;
};

/**
 * Computes the convex hull of a set of 2D points using a simple Graham scan.
 * Returns the hull as an array of THREE.Vector2, in counter-clockwise order.
 */
export const computeConvexHull2D = (points: THREE.Vector2[]): THREE.Vector2[] => {
  if (points.length <= 3) return points.slice();

  // Sort points lexicographically (first by x, then by y)
  const sorted = points.slice().sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));

  const cross = (o: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  // Remove the last point of each list (it's the same as the first point of the other list)
  upper.pop();
  lower.pop();

  return lower.concat(upper);
};
